import React, { useEffect, useId, useRef, useState } from 'react';
import { CHAT_FOLLOWUP } from '../constants';
import { tUi } from '../utils/uiText';

/**
 * Lets the user ask a specific question about part of (or all of) a consultant answer.
 */
export default function AssistantFollowUp({
  answerContent,
  messageIndex,
  selectedExcerpt,
  onClearSelection,
  onSubmit,
  disabled = false,
  uiLanguage = 'en',
}) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const questionId = useId();
  const questionRef = useRef(null);

  useEffect(() => {
    if (selectedExcerpt && selectedExcerpt.trim()) {
      setOpen(true);
    }
  }, [selectedExcerpt]);

  useEffect(() => {
    if (open) {
      questionRef.current?.focus();
    }
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = question.trim();
    if (!trimmed || disabled) return;
    onSubmit({
      question: trimmed,
      excerpt: selectedExcerpt?.trim() || '',
      answerContent,
    });
    setQuestion('');
    setOpen(false);
    onClearSelection?.();
  };

  const excerpt = selectedExcerpt?.trim();

  return (
    <div className="mt-3 pt-2 border-t border-teal-500/20">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          className="text-xs font-medium text-teal-300 hover:text-teal-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {CHAT_FOLLOWUP.toggleLabel}
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-2">
          <p className="text-[11px] text-gray-400">
            {CHAT_FOLLOWUP.hint}
          </p>
          {excerpt ? (
            <div className="rounded-md bg-gray-950 border border-cyan-500/25 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-cyan-400 mb-1">
                {CHAT_FOLLOWUP.excerptLabel}
              </p>
              <p className="text-sm text-gray-200 whitespace-pre-wrap">&ldquo;{excerpt}&rdquo;</p>
              <button
                type="button"
                onClick={() => onClearSelection?.(messageIndex)}
                className="mt-1 text-[11px] text-gray-500 hover:text-gray-300"
              >
                {CHAT_FOLLOWUP.clearExcerptLabel}
              </button>
            </div>
          ) : (
            <p className="text-[11px] text-gray-500 italic">
              {CHAT_FOLLOWUP.noExcerptHint}
            </p>
          )}
          <label htmlFor={questionId} className="sr-only">
            {CHAT_FOLLOWUP.questionLabel}
          </label>
          <textarea
            id={questionId}
            ref={questionRef}
            rows={2}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={CHAT_FOLLOWUP.questionPlaceholder}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm bg-black border border-teal-500/30 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-teal-500 resize-y min-h-[4rem]"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={disabled || !question.trim()}
              className="px-3 py-1.5 rounded-md text-xs font-semibold bg-teal-500 hover:bg-teal-400 text-black disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {CHAT_FOLLOWUP.sendLabel}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setQuestion('');
                onClearSelection?.(messageIndex);
              }}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-400 hover:text-gray-200"
            >
              {tUi(uiLanguage, 'followUpCancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
